import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { InvoiceLineItem } from "@jugl-web/rest-api";
import { getCurrencySymbol } from "../../../../../InvoiceForm/components/CurrencyField/utils";

function parsePrice(input: string): number {
  const cleaned = input.replace(",", ".").replace(/[^\d.]/g, "");

  // Match a number with optional decimal (up to 2 digits)
  const match = cleaned.match(/^0*(\d+)(\.\d{0,2})?/);
  if (!match) return 0;

  // Construct final number string without leading zeros
  const numberStr = match[1] + (match[2] || "");
  const num = parseFloat(numberStr);

  return Math.round(num * 100) / 100;
}

export const PriceCell: FC<{ item: InvoiceLineItem; idx: number }> = ({
  item,
  idx,
}) => {
  const [symbolPadding, setSymbolPadding] = useState(7);
  const symbolRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { formValues, itemsField } = useInvoicesItemFormPageContext();
  const [displayPrice, setDisplayPrice] = useState(
    item.price ? item.price.toString() : ""
  );
  const autoUpdateBlocked = useRef(false);
  useEffect(() => {
    if (autoUpdateBlocked.current) {
      return;
    }
    setDisplayPrice(item.price ? item.price.toString() : "0");
  }, [item]);

  const selectedCurrency = formValues.currency;

  const updateValuePosition = () => {
    if (symbolRef.current && inputRef.current) {
      const symbolWidth = symbolRef.current.offsetWidth;
      const gapSymbolValue = 20;
      setSymbolPadding(symbolWidth + gapSymbolValue);
    }
  };

  useLayoutEffect(() => {
    updateValuePosition();
  }, [item.price, selectedCurrency]);

  const handleUpdatePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDisplayPrice(value);
    itemsField.update(idx, {
      ...item,
      price: parsePrice(value),
    });
  };

  return (
    <div className="hover:bg-grey-100 relative flex h-full w-full items-center gap-1 px-4">
      <span ref={symbolRef} className="font-primary text-[#828282]">
        {getCurrencySymbol(selectedCurrency)}
      </span>

      <input
        ref={inputRef}
        type="text"
        maxLength={12}
        className="focus:border-primary-200 text-dark-800 absolute left-0 top-0 h-full w-full border border-solid border-transparent bg-transparent pr-4 text-sm font-medium focus:outline-none"
        style={{ paddingLeft: `${symbolPadding}px` }}
        value={displayPrice}
        onKeyDown={(e) => {
          const { key } = e;
          const allowedKeys = [
            "Backspace",
            "Delete",
            "Tab",
            "Escape",
            "Enter",
            "Home",
            "End",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
          ];
          if (
            // Allow navigation and editing keys
            !allowedKeys.includes(key) &&
            // Only allow numbers and one decimal separator
            (!/^[0-9.,]$/.test(key) ||
              // Prevent second decimal separator
              ((displayPrice.includes(".") || displayPrice.includes(",")) &&
                (key === "." || key === ",")) ||
              // Block if already has two digits after decimal
              (key !== "Backspace" &&
                displayPrice.match(/[.,]\d{2}$/) !== null))
          ) {
            e.preventDefault();
          }
        }}
        onChange={handleUpdatePrice}
        onFocus={() => {
          autoUpdateBlocked.current = true;
        }}
        onBlur={() => {
          autoUpdateBlocked.current = false;
          itemsField.update(idx, {
            ...item,
            price: parsePrice(displayPrice),
          });
        }}
      />
    </div>
  );
};
